// eslint-disable-next-line import/no-extraneous-dependencies
import pdfMake from 'pdfmake'
// eslint-disable-next-line import/extensions
import pdfFonts from 'pdfmake/build/vfs_fonts.js'
import html2canvas from 'html2canvas'
import { jsPDF as JsPDF } from 'jspdf'

class PdfService {
  private measureContext: CanvasRenderingContext2D | null = null

  private defaultOptionsJsPdf = {
    size: 1000,
    margin: 0,
    fileName: 'pdf.pdf',
  }

  private defaultOptionsPdfMake = {
    fileName: 'pdf.pdf',
  }

  async generateFromHtml(html: string, options = this.defaultOptionsJsPdf) {
    const { size, margin, fileName } = options

    const doc = new JsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    })

    const scale = (doc.internal.pageSize.width - (margin * 2)) / size
    await doc.html(html, {
      x: 0,
      y: 0,
      autoPaging: 'text',
      margin: [margin, margin, margin, margin],
      html2canvas: { scale },
    })

    doc.save(fileName)
  }

  async generateFromReferenceElement(element: string | HTMLElement, options: {
    orientation: 'p' | 'portrait' | 'l' | 'landscape' | undefined;
    fileName: string,
  } = { orientation: 'portrait', fileName: 'arquivo.pdf' }) {
    const { orientation, fileName } = options

    const document = new JsPDF({
      orientation,
      unit: 'px',
      format: 'a4',
      compress: true,
    })

    return new Promise<void>((resolve, reject) => {
      try {
        document.html('', {
          callback: (pdfInstance) => {
            pdfInstance.html(element, {
              x: 2,
              y: 2,
              html2canvas: {
                scale: 0.5,
                useCORS: true,
                scrollX: 0,
                scrollY: 0,
              },
              callback: (file) => {
                file.save(`${fileName}.pdf`)
                resolve()
              },
            })
          },
          html2canvas: {
            scale: 0,
            useCORS: true,
            scrollX: 0,
            scrollY: 0,
          },
          x: 0,
          y: 0,
        })
      } catch (error) {
        reject(error)
      }
    })
  }

  async generateFromHtmlWithMultiplePages(
    element: HTMLElement,
    options: {
    orientation: 'p' | 'portrait' | 'l' | 'landscape' | undefined;
    fileName: string;
    margin: number;
    uploadFile?: boolean;
  } = {
      orientation: 'portrait',
      fileName: 'arquivo.pdf',
      margin: 10,
      uploadFile: false,
    },
  ): Promise<Blob | void> {
    const {
      orientation,
      fileName,
      margin,
      uploadFile,
    } = options

    // MUITO importante se houver webfonts
    if ((window.document as any).fonts?.ready) {
      await (window.document as any).fonts.ready
    }

    // Garante que o navegador terminou um ciclo de layout/paint
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve())
    })

    const canvas = await html2canvas(element, {
      scale: 2,

      useCORS: true,

      scrollX: 0,
      scrollY: 0,

      backgroundColor: '#ffffff',

      logging: false,
    })

    const pdf = new JsPDF({
      orientation,
      unit: 'mm',
      format: 'a4',
      compress: true,
    })

    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()

    const contentWidth = pageWidth - (margin * 2)
    const contentHeight = pageHeight - (margin * 2)

    /*
   * Quantos pixels do canvas correspondem à altura útil
   * de uma página do PDF.
   */
    const pageHeightPx = Math.floor(
      canvas.width * (contentHeight / contentWidth),
    )

    const elementRect = element.getBoundingClientRect()
    const canvasPxPerCssPx = elementRect.width ? canvas.width / elementRect.width : 1
    const safeBreakOffsetsPx = this.getSafeBreakOffsetsPx(element, elementRect, canvasPxPerCssPx)

    let offsetY = 0
    let pageIndex = 0
    const pageStartOffsetsPx: number[] = []

    while (offsetY < canvas.height) {
      pageStartOffsetsPx.push(offsetY)

      const naiveSliceEnd = Math.min(offsetY + pageHeightPx, canvas.height)
      const safeSliceEnd = this.pickSafeSliceEnd(offsetY, naiveSliceEnd, canvas.height, safeBreakOffsetsPx)
      const sliceHeight = safeSliceEnd - offsetY

      const pageCanvas = window.document.createElement('canvas')

      pageCanvas.width = canvas.width
      pageCanvas.height = sliceHeight

      const context = pageCanvas.getContext('2d')

      if (!context) {
        throw new Error('Não foi possível criar contexto do canvas')
      }

      context.drawImage(
        canvas,

        // origem
        0,
        offsetY,

        // tamanho da região original
        canvas.width,
        sliceHeight,

        // destino
        0,
        0,
        canvas.width,
        sliceHeight,
      )

      if (pageIndex > 0) {
        pdf.addPage()
      }

      const renderedHeight = sliceHeight * (contentWidth / canvas.width)

      const image = pageCanvas.toDataURL('image/png')

      pdf.addImage(
        image,
        'PNG',
        margin,
        margin,
        contentWidth,
        renderedHeight,
        undefined,
        'FAST',
      )

      offsetY += sliceHeight
      pageIndex += 1
    }

    const pageStartOffsetsCssPx = pageStartOffsetsPx.map(offset => offset / canvasPxPerCssPx)

    this.addInvisibleTextLayer(pdf, element, {
      margin,
      contentWidth,
      pageStartOffsetsCssPx,
      totalPages: pageIndex,
    })

    if (uploadFile) {
      return pdf.output('blob')
    }

    pdf.save(`${fileName}.pdf`)
    return undefined
  }

  /*
   * Sobrepõe texto real (invisível) por cima da imagem rasterizada, na posição de cada
   * elemento-folha de texto do HTML de origem — permite selecionar/copiar/pesquisar o
   * texto sem alterar em nada o que é exibido (renderingMode 'invisible').
   *
   * Usa Range.getClientRects() (a mesma API que o navegador usa pra desenhar o highlight de
   * seleção) em vez do bounding box do elemento inteiro: cada retângulo já vem na posição real
   * de cada linha visual, respeitando quebra manual (<br>), quebra automática por largura e
   * alinhamento (center/right) — sem precisar recalcular nada disso na mão.
   */
  private addInvisibleTextLayer(
    pdf: JsPDF,
    element: HTMLElement,
    { margin, contentWidth, pageStartOffsetsCssPx, totalPages }: {
      margin: number;
      contentWidth: number;
      pageStartOffsetsCssPx: number[];
      totalPages: number;
    },
  ) {
    const elementRect = element.getBoundingClientRect()

    if (!elementRect.width || !elementRect.height || totalPages < 1) {
      return
    }

    const mmPerCssPx = contentWidth / elementRect.width

    const textElements = Array.from(element.querySelectorAll('*'))
      .filter((node): node is HTMLElement => this.isLeafTextElement(node))

    textElements.forEach((node) => {
      const computedStyle = window.getComputedStyle(node)
      const fontSizePt = (parseFloat(computedStyle.fontSize) || 10) * 0.75
      const font = `${computedStyle.fontStyle} ${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`

      this.splitIntoLineSegments(node).forEach((segmentNodes) => {
        this.renderLineSegment(pdf, segmentNodes, {
          elementRect,
          mmPerCssPx,
          pageStartOffsetsCssPx,
          margin,
          totalPages,
          fontSizePt,
          font,
        })
      })
    })
  }

  // Corta os filhos do elemento em blocos separados por <br> — cada bloco é uma "linha" pretendida
  private splitIntoLineSegments(node: HTMLElement): Text[][] {
    const segments: Text[][] = [[]]

    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeName === 'BR') {
        segments.push([])
        return
      }

      segments[segments.length - 1].push(child as Text)
    })

    return segments.filter(segment => segment.some(textNode => (textNode.textContent || '').trim()))
  }

  private renderLineSegment(
    pdf: JsPDF,
    segmentNodes: Text[],
    { elementRect, mmPerCssPx, pageStartOffsetsCssPx, margin, totalPages, fontSizePt, font }: {
      elementRect: DOMRect;
      mmPerCssPx: number;
      pageStartOffsetsCssPx: number[];
      margin: number;
      totalPages: number;
      fontSizePt: number;
      font: string;
    },
  ) {
    const range = document.createRange()
    const lastNode = segmentNodes[segmentNodes.length - 1]

    range.setStart(segmentNodes[0], 0)
    range.setEnd(lastNode, lastNode.length)

    const rects = Array.from(range.getClientRects()).filter(rect => rect.width > 0 && rect.height > 0)

    if (!rects.length) {
      return
    }

    const segmentText = segmentNodes.map(textNode => textNode.textContent || '').join('')
    const lineTexts = this.splitTextAcrossRects(segmentText, rects, font)

    rects.forEach((rect, index) => {
      const lineText = (lineTexts[index] || '').trim()

      if (!lineText) {
        return
      }

      const relativeLeft = rect.left - elementRect.left
      const relativeTop = rect.top - elementRect.top

      const rawPageIndex = this.resolvePageIndex(relativeTop, pageStartOffsetsCssPx)
      const pageNumber = Math.min(rawPageIndex, totalPages - 1) + 1
      const offsetInPage = relativeTop - pageStartOffsetsCssPx[rawPageIndex]

      pdf.setPage(pageNumber)
      pdf.setFontSize(fontSizePt)
      // Sem maxWidth: a linha já veio pronta do getClientRects() real do navegador — deixar o
      // jsPDF medir de novo (com as métricas da fonte dele, diferentes da fonte real) só faz
      // ele quebrar de novo, errado, achando que o texto não cabe.
      pdf.text(lineText, margin + (relativeLeft * mmPerCssPx), margin + (offsetInPage * mmPerCssPx), {
        baseline: 'top',
        renderingMode: 'invisible',
      })
    })
  }

  // Divide o texto entre as linhas reais medindo a largura de cada palavra com a fonte
  // real do elemento — mesma técnica de "greedy line breaking" que o navegador usa,
  // em vez de estimar por proporção de contagem de palavras (impreciso com palavras de
  // tamanho muito desigual, ex.: uma palavra longa isolada numa linha curta).
  private splitTextAcrossRects(text: string, rects: DOMRect[], font: string): string[] {
    const trimmed = text.replace(/\s+/g, ' ').trim()

    if (rects.length <= 1) {
      return [trimmed]
    }

    const words = trimmed.split(' ').filter(Boolean)
    const context = this.getMeasureContext()

    context.font = font

    const lines: string[] = []
    let wordIndex = 0

    rects.forEach((rect, index) => {
      if (index === rects.length - 1) {
        lines.push(words.slice(wordIndex).join(' '))
        return
      }

      const lineWords: string[] = []
      let lineWidth = 0

      while (wordIndex < words.length) {
        const word = words[wordIndex]
        const wordWidth = context.measureText(lineWords.length ? ` ${word}` : word).width

        if (lineWords.length && lineWidth + wordWidth > rect.width) {
          break
        }

        lineWords.push(word)
        lineWidth += wordWidth
        wordIndex += 1
      }

      // garante progresso mesmo se uma palavra sozinha já excede a largura do retângulo
      if (!lineWords.length && wordIndex < words.length) {
        lineWords.push(words[wordIndex])
        wordIndex += 1
      }

      lines.push(lineWords.join(' '))
    })

    return lines
  }

  private getMeasureContext(): CanvasRenderingContext2D {
    if (!this.measureContext) {
      this.measureContext = window.document.createElement('canvas').getContext('2d') as CanvasRenderingContext2D
    }

    return this.measureContext
  }

  private isLeafTextElement(node: Element): node is HTMLElement {
    if (!node.textContent || !node.textContent.trim()) {
      return false
    }

    return Array.from(node.childNodes).every(
      child => child.nodeType === Node.TEXT_NODE || child.nodeName === 'BR',
    )
  }

  /*
   * Pontos de corte seguros: topo de cada `header`/`footer`/`table`/`tr` (nunca corta uma
   * linha de tabela ao meio) + topo de cada filho direto de `main` (cada `<section>`/`<h3>`
   * do formulário é tratado como bloco atômico). Um filho de `main` só entra na lista se o
   * irmão anterior NÃO for um heading (h1-h6) — isso evita separar um título da seção de
   * dados logo abaixo dele, que ficaria orfão no fim da página.
   */
  private getSafeBreakOffsetsPx(element: HTMLElement, elementRect: DOMRect, canvasPxPerCssPx: number): number[] {
    const rowLevelNodes = Array.from(element.querySelectorAll('header, footer, table, tr'))

    const sectionContainers = Array.from(element.querySelectorAll('main'))
    const sectionContainerNodes = sectionContainers.length ? sectionContainers : [element]

    const sectionLevelNodes = sectionContainerNodes.flatMap(container => Array.from(container.children)
      .filter((child) => {
        const previous = child.previousElementSibling

        return !(previous && /^H[1-6]$/.test(previous.tagName))
      }))

    const offsets = [...rowLevelNodes, ...sectionLevelNodes]
      .map(node => Math.round((node.getBoundingClientRect().top - elementRect.top) * canvasPxPerCssPx))
      .filter(offset => offset > 0)

    return Array.from(new Set(offsets)).sort((a, b) => a - b)
  }

  // Escolhe o fim da fatia: a última página nunca recua; nas demais, usa o candidato seguro
  // mais próximo do limite da página — sem candidato no intervalo, cai no corte cru (fallback).
  private pickSafeSliceEnd(offsetY: number, naiveSliceEnd: number, canvasHeight: number, safeBreakOffsetsPx: number[]): number {
    if (naiveSliceEnd === canvasHeight) {
      return naiveSliceEnd
    }

    const candidatesInRange = safeBreakOffsetsPx.filter(offset => offset > offsetY && offset <= naiveSliceEnd)

    return candidatesInRange.length ? candidatesInRange[candidatesInRange.length - 1] : naiveSliceEnd
  }

  // Em qual página física (índice base 0) um deslocamento vertical cai — necessário porque,
  // com corte seguro, as páginas não têm mais altura uniforme entre si.
  private resolvePageIndex(relativeTop: number, pageStartOffsetsCssPx: number[]): number {
    let pageIndex = 0

    pageStartOffsetsCssPx.forEach((offset, index) => {
      if (offset <= relativeTop) {
        pageIndex = index
      }
    })

    return pageIndex
  }

  async generateFromDefinition(definition: any, options = this.defaultOptionsPdfMake) {
    (pdfMake as any).vfs = pdfFonts.pdfMake.vfs
    const pdf = (pdfMake as any).createPdf(definition)
    pdf.download(options.fileName)
  }
}

// eslint-disable-next-line import/prefer-default-export
export const pdfService = new PdfService()
